import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import startCase from 'lodash/startCase';
import orderBy from 'lodash/orderBy';
import debounce from 'lodash/debounce';
import styled from 'styled-components';

import Divider from '@twicapp/ui/components/Divider';
import Title2 from '@twicapp/ui/components/Title2';
import TextField from '@twicapp/ui/components/TextField';
import { ReactComponent as CloseSvg } from '@twicapp/ui/icons/close.svg';

import SideNavigationBar from './SideNavigationBar';
import SideLink from './SideLink';

import FeatureFlagContext from 'contexts/FeatureFlagContext';
import type { PretaxAccount } from 'contexts/PretaxContext/types';

import type {
  ICompanyWalletConfiguration,
  IEmployeeWallet,
} from 'hooks/useQueryProfile';

import { getWalletDisplayName } from 'utils/company';
import { getPretaxAccountLabel } from 'utils/pretaxAccount';
import { logEvent, EVENTS } from 'utils/amplitude';
import { StoreRoutes } from 'utils/routeGenerator';

import WalletTypeIcon from 'components/common/WalletIcon';
import { ReactComponent as FSAHSAIcon } from 'assets/images/icons/healthkit.svg';

import { PRETAX_ACCOUNT_TYPES } from 'constants/accounts';
import { FEATURE_FLAGS } from 'constants/featureFlag';
import { STORE_LAYOUT_ELEMENTS } from 'constants/testSelectors';
import {
  SVG_ICON_SIZES,
  SVG_ICON_COLORS,
} from '@twicapp/ui/constants/properties';

const SEARCH_PATH = '/search';

const Container = styled.div.attrs({ role: 'navigation' })`
  width: 182px;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-top: 16px;
`;

const AccountContainer = styled.div`
  width: 180px;
  margin-left: -13px;
`;

const PreTaxAccountContainer = styled.div`
  margin-top: -4px;
`;

const Section = styled.div``;

const HorizontalSeparator = styled.div`
  width: 147px;
  margin-top: 12px;
  margin-bottom: 24px;
`;

const StyledFSAHSAIcon = styled(FSAHSAIcon)`
  margin-right: 5px;
  margin-top: 2px;
`;

const SuggestionContainer = styled.div`
  width: 175px;
  height: 182px;
`;

const SuggestionQuestion = styled(Title2)`
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const FormLinkContainer = styled.div`
  margin-top: 13px;
  width: 143px;
`;

const FormLinkText = styled.a.attrs({ target: '_blank' })`
  text-decoration: none;
  :hover {
    text-decoration: none;
  }
  color: ${({
    theme: {
      colors: {
        text: { interactive },
      },
    },
  }) => interactive};
  font-size: 16px;
  line-height: 24px;
`;

const FormText = styled.span`
  padding-left: 5px;
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
  font-size: 16px;
  line-height: 24px;
`;

const CategoryContainer = styled.div`
  width: 180px;
  margin-left: -13px;
`;

const SearchInput = styled(TextField)`
  width: 100%;
  margin-bottom: 36px;
`;

const onAccountClick = ({ wallet_name }: $TSFixMe) => {
  logEvent(EVENTS.StoreViewed, {
    account_filter: wallet_name,
    category_filter: '',
  });
};

const onCategoryClick = ({ name, category_alias, wallet_name }: $TSFixMe) => {
  logEvent(EVENTS.StoreViewed, {
    account_filter: wallet_name,
    category_filter: category_alias ? category_alias : name,
  });
};

const search = (query: string, history: ReturnType<typeof useHistory>) => {
  const route = StoreRoutes.search({ query });
  if (history.location.pathname === SEARCH_PATH) {
    history.replace(route);
  } else {
    history.push(route);
  }
};
const debouncedSearch = debounce(search, 400);

const Search = () => {
  const feature_flag = useContext(FeatureFlagContext);
  const history = useHistory();
  const initial_search_query =
    new URLSearchParams(history.location.search).get('q') ?? '';
  const [search_query, setSearchQuery] = React.useState(initial_search_query);
  useEffect(() => {
    if (history.location.pathname !== SEARCH_PATH) {
      setSearchQuery('');
    }
  }, [history.location.pathname]);
  const search_on_keystroke =
    feature_flag.flag(FEATURE_FLAGS.SEARCH_ON_ENTER_VS_KEYSTROKE) ===
    'keystroke';
  const handleSearchOnKeystroke = (query: string) => {
    setSearchQuery(query);
    if (search_on_keystroke) {
      debouncedSearch(query, history);
    }
  };
  const handleSearchOnEnter = (e: React.KeyboardEvent) => {
    if (!search_on_keystroke && e.key === 'Enter') {
      search(search_query, history);
    }
  };

  return (
    <div onKeyDown={handleSearchOnEnter}>
      <SearchInput
        onChange={handleSearchOnKeystroke}
        value={search_query}
        label="Search"
        end_icon={search_query ? CloseSvg : undefined}
        onClickEndIcon={() => handleSearchOnKeystroke('')}
      />
    </div>
  );
};

const CategoryFilters = ({
  employee_wallets,
  filtered_wallet,
  categories_with_vendors,
  pretax_accounts,
  has_fsa_and_lpf,
}: {
  employee_wallets: IEmployeeWallet[];
  filtered_wallet: IEmployeeWallet;
  categories_with_vendors: ICompanyWalletConfiguration['categories'];
  pretax_accounts: PretaxAccount[];
  has_fsa_and_lpf: boolean;
}) => {
  const feature_flag = useContext(FeatureFlagContext);
  const { company_wallet_configuration } = filtered_wallet;
  const account_type =
    pretax_accounts[0] && has_fsa_and_lpf
      ? 'LPFSA & HSA'
      : pretax_accounts[0]?.account_type;

  return (
    <Container>
      {feature_flag.flag(FEATURE_FLAGS.INITIAL_SEARCH_LAUNCH) && <Search />}
      <Section>
        <AccountContainer>
          <SideNavigationBar header="Browse by Account">
            {employee_wallets.map(
              ({
                id,
                company_wallet_configuration: { wallet_name },
                wallet_type_id,
              }: $TSFixMe) => (
                <SideLink
                  key={id}
                  route={StoreRoutes.listFeatured({
                    employee_wallet_id: id,
                    featured_section: wallet_name,
                  })}
                  start_icon={
                    <WalletTypeIcon
                      wallet_type_id={wallet_type_id}
                      size={SVG_ICON_SIZES.MEDIUM}
                      stroke={SVG_ICON_COLORS.PRIMARY}
                    />
                  }
                  onClick={() => onAccountClick({ wallet_name })}
                >
                  {getWalletDisplayName(wallet_name)}
                </SideLink>
              )
            )}
          </SideNavigationBar>
          {pretax_accounts.length > 0 && (
            <PreTaxAccountContainer>
              <SideNavigationBar>
                {has_fsa_and_lpf ? (
                  <SideLink
                    key="LPFSAHSA"
                    start_icon={<StyledFSAHSAIcon />}
                    route={StoreRoutes.detailPretax({
                      pretax_account: PRETAX_ACCOUNT_TYPES.LPFSAHSA,
                    })}
                  >
                    {'LPFSA & HSA'}
                  </SideLink>
                ) : (
                  <SideLink
                    key={getPretaxAccountLabel({ account_type })}
                    start_icon={<StyledFSAHSAIcon />}
                    route={StoreRoutes.detailPretax({
                      pretax_account: getPretaxAccountLabel({
                        account_type,
                      }),
                    })}
                  >
                    {getPretaxAccountLabel({ account_type })}
                  </SideLink>
                )}
              </SideNavigationBar>
            </PreTaxAccountContainer>
          )}
        </AccountContainer>
      </Section>
      <HorizontalSeparator>
        <Divider />
      </HorizontalSeparator>
      <CategoryContainer data-testid={STORE_LAYOUT_ELEMENTS.SUBCATEGORY_LINK}>
        <SideNavigationBar header="Browse by Category">
          {orderBy(
            categories_with_vendors,
            [
              (categories: typeof categories_with_vendors[0]) =>
                categories.alias
                  ? categories.alias.toLowerCase()
                  : categories.name.toLowerCase(),
            ],
            'asc'
          ).map(({ name, id, alias }) => (
            <SideLink
              key={id}
              category_id={id}
              route={StoreRoutes.listCategory({
                employee_wallet_id: filtered_wallet.id,
                category_id: id,
                alias: alias ? alias : id,
              })}
              onClick={() =>
                onCategoryClick({
                  alias,
                  name,
                  wallet_name: company_wallet_configuration.wallet_name,
                })
              }
            >
              {alias ? startCase(alias) : startCase(name)}
            </SideLink>
          ))}
        </SideNavigationBar>
      </CategoryContainer>
      <HorizontalSeparator>
        <Divider />
      </HorizontalSeparator>
      <SuggestionContainer>
        <SuggestionQuestion>{`Can't find something?`}</SuggestionQuestion>
        <FormLinkContainer>
          <FormLinkText href="https://twic.typeform.com/to/QjUpdaVG">
            Submit a request
          </FormLinkText>
          <FormText>
            {`to the Twic team and tell us what you'd love to see that's currently missing.`}
          </FormText>
        </FormLinkContainer>
      </SuggestionContainer>
    </Container>
  );
};

export default CategoryFilters;
